import styled from "styled-components";
import ChevronRightIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronRightIconPath";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";

const ArticleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  border: 1px solid var(--grey200);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
`;

const ArticleCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ArticleCardImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  background-color: #f5f5f5;
  border-radius: 4px;
`;

const ArticleCardImagePlaceholder = styled.div`
  width: 72px;
  height: 72px;
  background-color: #f5f5f5;
  border-radius: 4px;
  flex-shrink: 0;
`;

const ArticleCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ArticleCardName = styled.div`
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 1rem;
`;

const ArticleCardNumber = styled.div`
  background-color: #111111;
  color: #ffffff;
  font-family: "IKEA FONT VARIABLE", "Noto IKEA", sans-serif;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 2px 8px;
  border-radius: 2px;
  display: inline-block;
`;

const ArticleCardChevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--grey300);
  border-radius: 50%;
  flex-shrink: 0;
  cursor: pointer;
`;

export type ImpactedArticleSummary = {
  productArticleId: string;
  fallbackImageUrl?: string;
};

interface ImpactedArticleCardProps {
  article: ImpactedArticleSummary;
  showChevron?: boolean;
  onChevronClick?: () => void;
}

export const ImpactedArticleCard = ({
  article,
  showChevron = false,
  onChevronClick,
}: ImpactedArticleCardProps) => {
  const { data: itemInfo } = useItemInfo(article.productArticleId);
  const name = itemInfo?.name ?? article.productArticleId;
  const imageUrl = itemInfo?.smallImageUrl || article.fallbackImageUrl || "";

  return (
    <ArticleCard>
      <ArticleCardLeft>
        {imageUrl ? (
          <ArticleCardImage src={imageUrl} alt={name} />
        ) : (
          <ArticleCardImagePlaceholder />
        )}
        <ArticleCardInfo>
          <ArticleCardName>{name}</ArticleCardName>
          <ArticleCardNumber>{article.productArticleId}</ArticleCardNumber>
        </ArticleCardInfo>
      </ArticleCardLeft>
      {showChevron && onChevronClick && (
        <ArticleCardChevron
          onClick={onChevronClick}
          role="button"
          tabIndex={0}
          aria-label="Next impacted article"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onChevronClick();
            }
          }}
        >
          <FixaSSRIcon paths={ChevronRightIconPath} />
        </ArticleCardChevron>
      )}
    </ArticleCard>
  );
};
